import {
  Chip,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import FilterComponent from 'lib/common/FilterNonSearch';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getBankAccountsForNominee,
  getPendingAccountsAvailablesForPaymentRequest,
} from '../../../../../../../../../../lib/api';
import { createPaymentRequestPassive } from '../../../../../../../../../../lib/api/payments';
import CustomButton from '../../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../../../../../lib/designSystem/Table';
import DocumentsSuggestedContainer from '../../../../../../../Financing/common/documentSugestion';
import GenerateNominee from '../../../../../../../Financing/Factoring/components/Recieved/components/ShoppingDialog/components/PaymentModule/components/generateNominee';

interface PendingAccount {
  id: number;
  businessName: string;
  businessIdentifier: string;
  amount: number;
  totalRemaining: number;
  amountToConciliate: number;
  amountAvailable: number;
}

const nomineeHeaders = [
  { key: 'bankAccountOriginName', label: 'Banco origen' },
  {
    key: 'bankAccountDestinationName',
    label: 'Banco destino',
  },
  { key: 'bankAccountDestinationNumber', label: 'N° Cuenta' },
  { key: 'bankAccountDestinationAccountType', label: 'Tipo de cuenta' },
  { key: 'bankAccountDestinationHolderName', label: 'Titular' },
  { key: 'bankAccountDestinationHolderIdentifier', label: 'Rut' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Ejecutada', type: 'date' },
];

const documentToApplyHeaders = [
  { key: 'folio', label: 'Folio' },
  { key: 'businessName', label: 'Empresa' },
  { key: 'stakeholderName', label: 'Deudor' },
  { key: 'amountToConciliate', label: 'Monto a conciliar', type: 'money' },
];

const PayoutPendingAccounts = (props: any) => {
  const { setOpen, setSuccessOpen, setFailureOpen, setUp } = props;
  const [loading, setLoading] = useState(false);
  const [selectedPendingAccounts, setSelectedPendingAccounts] = useState<any>(
    [],
  );
  const [pendingAccounts, setPendingAccounts] = useState<PendingAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<PendingAccount[]>(
    [],
  );
  const [pendingAccountsConfirmed, setPendingAccountsConfirmed] =
    useState(false);
  const [destinyAccounts, setDestinyAccounts] = useState([]);
  const [originAccounts, setOriginAccounts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [step, setStep] = useState(1);
  const [documentToApplySelected, setDocumentToApplySelected] = useState<any>(
    [],
  );
  const [documents, setDocuments] = useState([]);
  const [amountToApply, setAmountToApply] = useState(0);
  const [documentsToApply, setDocumentsToApply] = useState([]);
  const [maxAmount, setMaxAmount] = useState(0);
  const handleAmountToApply = (amount: string) => {
    setAmountToApply(parseInt(amount));
  };

  const handleStepBack = () => {
    setStep(step - 1);
    setPendingAccountsConfirmed(false);
  };

  const handleChangeAmountToConciliate = (event: any) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    const document = pendingAccounts.find(
      (inv: any) => inv.id === event.target.name,
    );
    if (!document) return;
    if (isNaN(amount)) return;

    if (amount > document.amountAvailable) {
      showAlert(
        dispatch,
        'warning',
        'El monto a conciliar no puede ser mayor al monto disponible',
      );
      return;
    }

    document.amountToConciliate = amount;
    setDocuments([...documents]);
    setMaxAmount(getMaxAmount());
  };

  const pendingAccountsHeaders = [
    { key: 'folio', label: 'Folio' },
    { key: 'businessName', label: 'Empresa' },
    { key: 'category', label: 'Categoría' },
    { key: 'observation', label: 'Observación' },
    { key: 'issuedDate', label: 'Fecha', type: 'date' },
    { key: 'amount', label: 'Monto original', type: 'money' },
    { key: 'amount', label: 'Monto disp.', type: 'money' },
    ...(step === 1
      ? [
          {
            key: 'amountToConciliate',
            label: 'Monto a descontar',
            handleChange: handleChangeAmountToConciliate,
            type: 'money',
          },
        ]
      : [
          {
            key: 'amountToConciliate',
            label: 'Monto a descontar',
            type: 'money',
          },
        ]),
  ];

  const dispatch = useDispatch();

  const setUpDocuments = async () => {
    setLoading(true);
    try {
      const response =
        await getPendingAccountsAvailablesForPaymentRequest(dispatch);
      setPendingAccounts(response);
      setFilteredAccounts(response);
    } catch (error) {
      alert('Error al cargar los documentos');
    }
    setLoading(false);
  };

  useEffect(() => {
    setUpDocuments();
  }, []);

  const getStakeholderData = () => {
    if (selectedPendingAccounts.length) {
      const document: any = pendingAccounts.find(
        (invoice: any) => invoice.id === selectedPendingAccounts[0],
      );
      if (document) {
        return {
          businessName: document.businessName,
          businessIdentifier: document.businessIdentifier,
        };
      }
    }
    return { businessName: '', businessIdentifier: '' };
  };

  const { businessIdentifier, businessName } = selectedPendingAccounts.length
    ? getStakeholderData()
    : {
        businessIdentifier: '',
        businessName: '',
      };

  const configurePaymentRequest = () => {
    const pendingAccountsFiltered = pendingAccounts.filter((doc: any) => {
      return selectedPendingAccounts.includes(doc.id);
    });
    const amount = pendingAccountsFiltered.reduce(
      (acc: number, doc: any) => acc + Number(doc.amountToConciliate),
      0,
    );
    return {
      businessName,
      amount: amount - amountToApply,
      documents: pendingAccountsFiltered,
    };
  };

  const validate = (request: any) => {
    if (selectedPendingAccounts.length === 0) {
      setFailureOpen('Debe seleccionar al menos un documento');
      return false;
    }
    console.log(request.amount, amountToApply, amountToApply);
    if (request.amount < 0) {
      setFailureOpen(
        'El monto a aplicar es mayor al monto de las cuentas pendientes',
      );
      return false;
    }

    return true;
  };
  const setUpConfigurePayment = async () => {
    const request = configurePaymentRequest();
    if (!validate(request)) {
      return false;
    }
    setLoading(true);
    setSelectedRequest(request);
    if (request.amount === 0) {
      setPendingAccountsConfirmed(true);
      setLoading(false);
      return true;
    }
    try {
      const accounts = await getBankAccountsForNominee(
        dispatch,
        businessIdentifier,
      );
      if (accounts) {
        setDestinyAccounts(accounts.businessBankAccounts);
        setOriginAccounts(accounts.ownBankAccounts);
        setPendingAccountsConfirmed(true);
        setLoading(false);
        return true;
      }
    } catch (error) {
      setFailureOpen('Error al obtener las cuentas');
      setLoading(false);
      return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const documentsToPay = pendingAccounts.filter((doc: any) => {
        return selectedPendingAccounts.includes(doc.id);
      });
      await createPaymentRequestPassive(dispatch, {
        payments,
        documentsToApply: documentsToApply.map((doc: any) => {
          return {
            id: doc.id,
            amountToConciliate: doc.amountToConciliate,
          };
        }),
        documentsToPay,
      });
      await setUp();
      setSuccessOpen('Pago configurado con éxito');
    } catch (error) {
      setFailureOpen('Error al configurar el pago');
    }
    setLoading(false);
    setOpen(false);
  };

  const stepChecker = async () => {
    switch (step) {
      case 1: {
        const canPass = await setUpConfigurePayment();
        if (!canPass) return;
        return setStep(2);
      }
      case 2:
        return await handleSubmit();
    }
  };

  const cantSelectRow = (row: any) => {
    if (businessIdentifier && row.businessIdentifier !== businessIdentifier)
      return true;
    return false;
  };

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <FilterComponent
          data={pendingAccounts}
          filterFields={[{ key: 'businessName', filterName: 'Nombre' }]}
          setFilteredData={setFilteredAccounts}
        />
      ),
    },
  ];

  const getMaxAmount = () => {
    return selectedPendingAccounts.reduce((acc: any, doc: any) => {
      const document: any = pendingAccounts.find((x: any) => x.id === doc);
      if (!document) return acc;
      return acc + Number(document?.amountToConciliate);
    }, 0);
  };

  useEffect(() => {
    if (selectedPendingAccounts.length) {
      const max = getMaxAmount();
      setMaxAmount(max);
    }
  }, [selectedPendingAccounts]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  const totalAmountToConciliate = pendingAccounts.reduce(
    (acc: any, doc: any) => {
      if (selectedPendingAccounts.includes(doc.id)) {
        return acc + Number(doc.amountToConciliate);
      }
      return acc;
    },
    0,
  );

  const disabledCheck = () => {
    if (totalAmountToConciliate - amountToApply < 0) return true;

    if (selectedRequest && selectedRequest.amount === 0) return false;

    if (selectedPendingAccounts.length === 0) return true;

    if (payments.length === 0 && pendingAccountsConfirmed) return true;

    return false;
  };

  return (
    <div>
      <DialogContent style={{ padding: '0px 24px' }}>
        {step === 1 && (
          <div>
            <HeaderWithActions
              title="Seleccione las cuentas pendientes a pagar"
              variant="body1"
              type="subHeader"
              buttons={buttonsData}
            />
            <EnhancedTable
              data={filteredAccounts}
              dataHeaders={pendingAccountsHeaders}
              selected={selectedPendingAccounts}
              setSelected={setSelectedPendingAccounts}
              rowsPerPageDefault={5}
              cantSelectRow={cantSelectRow}
              exportToFile="xlsx"
            />
            <div style={{ padding: '16px 0px' }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Monto a descontar
                  </Typography>
                  <TextField
                    fullWidth
                    disabled
                    value={formatMoney(totalAmountToConciliate)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiInputBase-input': {
                        textAlign: 'right',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Aplicar documentos
                  </Typography>
                  <DocumentsSuggestedContainer
                    businessIdentifier={businessIdentifier}
                    amountToApply={amountToApply}
                    setAmountToApply={handleAmountToApply}
                    selected={documentToApplySelected}
                    setSelected={setDocumentToApplySelected}
                    documents={documents}
                    maxAmount={maxAmount}
                    setDocuments={setDocuments}
                    setDocumentsToApply={setDocumentsToApply}
                    documentsToApply={documentsToApply}
                    operationDate={new Date().toISOString()}
                    smallTextField
                  />
                </Grid>
              </Grid>
            </div>
            <div
              style={{
                padding: '12px 0px',
                marginTop: '8px',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Grid container alignItems={'center'} spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="body1" margin={'4px'} fontWeight="bold">
                    Total a girar:
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <div style={{ textAlign: 'right' }}>
                    <Chip
                      label={formatMoney(
                        totalAmountToConciliate - amountToApply,
                      )}
                      color={
                        totalAmountToConciliate - amountToApply >= 0
                          ? 'success'
                          : 'error'
                      }
                      variant="outlined"
                      sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
        )}
        {step === 2 && !payments.length ? (
          <div>
            {selectedRequest.amount === 0 ? (
              <div>
                <Typography variant="body1">
                  Debido a que el monto a aplicar es igual al monto de las
                  cuentas pendientes, no es necesario realizar un pago. Presione
                  finalizar y quedarán cuadradas las cuentas a pagar con las
                  cuentas pendientes.
                </Typography>
                <div style={{ padding: '12px 0px' }}>
                  <Typography variant="body1" margin={'4px'}>
                    Documentos por pagar
                  </Typography>
                  <EnhancedTable
                    dataHeaders={pendingAccountsHeaders}
                    data={selectedRequest.documents}
                  />
                </div>
                <div style={{ padding: '12px 0px' }}>
                  <Typography variant="body1" margin={'4px'}>
                    Documentos aplicados
                  </Typography>
                  <EnhancedTable
                    dataHeaders={documentToApplyHeaders}
                    data={documentsToApply}
                  />
                </div>
              </div>
            ) : (
              <GenerateNominee
                availableAccounts={destinyAccounts}
                originAccounts={originAccounts}
                selectedRequest={selectedRequest}
                setPayments={setPayments}
                amount={selectedRequest.amount}
              />
            )}
          </div>
        ) : null}
        {step === 2 && payments.length ? (
          <div>
            <Typography variant="body1" margin={'4px'}>
              Seleccione la configuración de pago
            </Typography>
            <EnhancedTable dataHeaders={nomineeHeaders} data={payments} />
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="secondary"
          onClick={() => (step === 1 ? setOpen(false) : handleStepBack())}
        >
          {' '}
          Cancelar{' '}
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={() => stepChecker()}
          disabled={disabledCheck()}
        >
          {' '}
          {step === 1 ? 'Siguiente' : 'Finalizar'}{' '}
        </CustomButton>
      </DialogActions>
    </div>
  );
};

export default PayoutPendingAccounts;
